import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import {
  cloneDispatchFreightDraft,
  createEmptyDispatchFreightDraft,
} from '../../dispatch-freight/domain/dispatch-freight.builder';
import type {
  DispatchCreateCurrentQuoteRequest,
  DispatchCreateCurrentQuoteResponse,
  DispatchCurrentQuoteSummary,
  DispatchFreightContext,
  DispatchFreightDraft,
  DispatchPreferredRateMethod,
  DispatchRateLocation,
  DispatchRateQuoteResponse,
  DispatchRateUiState,
  DispatchRatingRequest,
} from '../../dispatch-freight/models/dispatch-freight.models';
import type { SpotQuoteConditionsDraft } from '../spot-quote/spot-quote.models';

export type FreightCommandLaneRole = 'origin' | 'destination';
export type FreightCommandQuoteCreateState =
  | 'idle'
  | 'creating'
  | 'created'
  | 'error'
  | 'conflict';

export interface FreightCommandQuoteCreateCommand {
  request: DispatchCreateCurrentQuoteRequest;
  idempotency_key: string;
  fingerprint: string;
}

export interface FreightCommandWorkspaceState {
  customer_contact_uuid: string;
  customer_name: string;
  context: DispatchFreightContext;
  draft: DispatchFreightDraft;
  preferred_rate_method: DispatchPreferredRateMethod | null;
  rating_request: DispatchRatingRequest | null;
  rate_state: DispatchRateUiState;
  rate_response: DispatchRateQuoteResponse | null;
  rate_error: string;
  conditions: SpotQuoteConditionsDraft;
  authoritative_conditions_accepted: boolean;
  quote_create_state: FreightCommandQuoteCreateState;
  quote_create_result: DispatchCurrentQuoteSummary | null;
  quote_create_error: string;
  quote_create_idempotency_key: string | null;
  quote_create_fingerprint: string | null;
}

@Injectable()
export class FreightCommandWorkspaceService {
  private readonly workspaceSubject = new BehaviorSubject<FreightCommandWorkspaceState>(
    this.createInitialState(),
  );

  readonly state$: Observable<FreightCommandWorkspaceState> =
    this.workspaceSubject.asObservable();

  get snapshot(): FreightCommandWorkspaceState {
    return this.cloneState(this.workspaceSubject.value);
  }

  reset(): void {
    this.workspaceSubject.next(this.createInitialState());
  }

  setCustomer(customerContactUuid: string, customerName = ''): void {
    const current = this.workspaceSubject.value;
    const uuid = customerContactUuid.trim();
    const name = customerName.trim();
    const identityChanged = uuid !== current.customer_contact_uuid;

    const next: FreightCommandWorkspaceState = {
      ...current,
      customer_contact_uuid: uuid,
      customer_name: name,
      context: {
        ...current.context,
        customer_contact_uuid: uuid || undefined,
        origin: { ...current.context.origin },
        destination: { ...current.context.destination },
      },
    };

    this.workspaceSubject.next(identityChanged ? this.withRateCleared(next) : next);
  }

  setLocation(role: FreightCommandLaneRole, location: DispatchRateLocation): void {
    const current = this.workspaceSubject.value;
    const nextLocation = this.cloneLocation(location);
    const locationChanged = JSON.stringify(current.context[role]) !== JSON.stringify(nextLocation);
    if (!locationChanged) return;

    this.workspaceSubject.next(this.withRateCleared({
      ...current,
      context: {
        ...current.context,
        origin: role === 'origin' ? nextLocation : { ...current.context.origin },
        destination: role === 'destination' ? nextLocation : { ...current.context.destination },
      },
    }));
  }

  setDraft(draft: DispatchFreightDraft): void {
    const current = this.workspaceSubject.value;
    const nextDraft = cloneDispatchFreightDraft(draft);

    this.workspaceSubject.next(this.withRateCleared({
      ...current,
      draft: nextDraft,
      preferred_rate_method: nextDraft.freight.preferred_rate_method,
    }));
  }

  setPreferredRateMethod(method: DispatchPreferredRateMethod | null): void {
    const current = this.workspaceSubject.value;
    if (
      current.preferred_rate_method === method
      && current.draft.freight.preferred_rate_method === method
    ) {
      return;
    }

    this.workspaceSubject.next(this.withRateCleared({
      ...current,
      preferred_rate_method: method,
      draft: {
        ...current.draft,
        freight: {
          ...current.draft.freight,
          preferred_rate_method: method,
        },
        items: current.draft.items.map((item) => ({ ...item })),
      },
    }));
  }

  beginRate(request: DispatchRatingRequest): void {
    const current = this.workspaceSubject.value;
    this.workspaceSubject.next(this.withQuoteCreateCleared({
      ...current,
      rating_request: this.cloneRatingRequest(request),
      rate_state: 'loading',
      rate_response: null,
      rate_error: '',
    }));
  }

  completeRate(request: DispatchRatingRequest, response: DispatchRateQuoteResponse): void {
    const current = this.workspaceSubject.value;
    if (!this.isCurrentRatingRequest(request, current.rating_request)) return;

    this.workspaceSubject.next(this.withQuoteCreateCleared({
      ...current,
      rating_request: this.cloneRatingRequest(request),
      rate_state: response.ok && response.status === 'rated' ? 'rated' : 'needs_input',
      rate_response: response,
      rate_error: '',
    }));
  }

  failRate(request: DispatchRatingRequest, message: string): void {
    const current = this.workspaceSubject.value;
    if (!this.isCurrentRatingRequest(request, current.rating_request)) return;

    this.workspaceSubject.next(this.withQuoteCreateCleared({
      ...current,
      rating_request: this.cloneRatingRequest(request),
      rate_state: 'error',
      rate_response: null,
      rate_error: message.trim() || 'Unable to retrieve an authoritative customer rate.',
    }));
  }

  clearRate(): void {
    this.workspaceSubject.next(this.withRateCleared(this.workspaceSubject.value));
  }

  setConditions(conditions: SpotQuoteConditionsDraft): void {
    const current = this.workspaceSubject.value;
    this.workspaceSubject.next({
      ...current,
      conditions: this.cloneConditions(conditions),
    });
  }

  setAuthoritativeConditionsAccepted(accepted: boolean): void {
    const current = this.workspaceSubject.value;
    const canAccept = this.currentQuoteRequest(current, true) !== null;
    this.workspaceSubject.next({
      ...this.withQuoteCreateCleared(current),
      authoritative_conditions_accepted: accepted && canAccept,
    });
  }

  prepareCurrentQuoteCreate(generatedIdempotencyKey: string): FreightCommandQuoteCreateCommand | null {
    const current = this.workspaceSubject.value;
    if (current.quote_create_state === 'creating' || current.quote_create_state === 'created') {
      return null;
    }
    const request = this.currentQuoteRequest(current, false);
    if (!request) return null;

    const fingerprint = JSON.stringify(request);
    const candidateKey = generatedIdempotencyKey.trim();
    const idempotencyKey = current.quote_create_fingerprint === fingerprint
      && current.quote_create_idempotency_key
      ? current.quote_create_idempotency_key
      : candidateKey;
    if (idempotencyKey.length < 8) return null;

    this.workspaceSubject.next({
      ...current,
      quote_create_state: 'creating',
      quote_create_result: null,
      quote_create_error: '',
      quote_create_idempotency_key: idempotencyKey,
      quote_create_fingerprint: fingerprint,
    });
    return { request, idempotency_key: idempotencyKey, fingerprint };
  }

  completeCurrentQuoteCreate(
    command: FreightCommandQuoteCreateCommand,
    response: DispatchCreateCurrentQuoteResponse,
  ): void {
    const current = this.workspaceSubject.value;
    if (!this.isCurrentQuoteCommand(current, command)) return;
    this.workspaceSubject.next({
      ...current,
      quote_create_state: 'created',
      quote_create_result: { ...response.data },
      quote_create_error: '',
    });
  }

  failCurrentQuoteCreate(command: FreightCommandQuoteCreateCommand, message: string): void {
    const current = this.workspaceSubject.value;
    if (!this.isCurrentQuoteCommand(current, command)) return;
    this.workspaceSubject.next({
      ...current,
      quote_create_state: 'error',
      quote_create_result: null,
      quote_create_error: message.trim() || 'Unable to create the quote.',
    });
  }

  conflictCurrentQuoteCreate(command: FreightCommandQuoteCreateCommand, message: string): void {
    const current = this.workspaceSubject.value;
    if (!this.isCurrentQuoteCommand(current, command)) return;
    this.workspaceSubject.next({
      ...this.withRateCleared(current),
      quote_create_state: 'conflict',
      quote_create_error: `${message.trim() || 'The authoritative rate changed.'} Check the rate again before retrying.`,
    });
  }

  rejectCurrentQuoteCreate(message: string): void {
    this.workspaceSubject.next({
      ...this.withQuoteCreateCleared(this.workspaceSubject.value),
      quote_create_state: 'error',
      quote_create_error: message.trim() || 'Review the authoritative rate and conditions before creating the quote.',
    });
  }

  private createInitialState(): FreightCommandWorkspaceState {
    return {
      customer_contact_uuid: '',
      customer_name: '',
      context: {
        origin: {},
        destination: {},
      },
      draft: createEmptyDispatchFreightDraft(),
      preferred_rate_method: null,
      rating_request: null,
      rate_state: 'idle',
      rate_response: null,
      rate_error: '',
      conditions: {
        currency: 'CAD',
        valid_until: '',
        service_level: '',
        equipment_notes: '',
        customer_notes: '',
        internal_notes: '',
      },
      authoritative_conditions_accepted: false,
      quote_create_state: 'idle',
      quote_create_result: null,
      quote_create_error: '',
      quote_create_idempotency_key: null,
      quote_create_fingerprint: null,
    };
  }

  private withRateCleared(
    state: FreightCommandWorkspaceState,
  ): FreightCommandWorkspaceState {
    return this.withQuoteCreateCleared({
      ...state,
      rating_request: null,
      rate_state: 'idle',
      rate_response: null,
      rate_error: '',
    });
  }

  private withQuoteCreateCleared(
    state: FreightCommandWorkspaceState,
  ): FreightCommandWorkspaceState {
    return {
      ...state,
      authoritative_conditions_accepted: false,
      quote_create_state: 'idle',
      quote_create_result: null,
      quote_create_error: '',
      quote_create_idempotency_key: null,
      quote_create_fingerprint: null,
    };
  }

  private cloneState(state: FreightCommandWorkspaceState): FreightCommandWorkspaceState {
    return {
      ...state,
      context: {
        ...state.context,
        origin: this.cloneLocation(state.context.origin),
        destination: this.cloneLocation(state.context.destination),
      },
      draft: cloneDispatchFreightDraft(state.draft),
      rating_request: state.rating_request
        ? this.cloneRatingRequest(state.rating_request)
        : null,
      conditions: this.cloneConditions(state.conditions),
      quote_create_result: state.quote_create_result
        ? { ...state.quote_create_result }
        : null,
    };
  }

  private cloneLocation(location: DispatchRateLocation): DispatchRateLocation {
    return { ...location };
  }

  private cloneRatingRequest(request: DispatchRatingRequest): DispatchRatingRequest {
    return {
      ...request,
      origin: this.cloneLocation(request.origin),
      destination: this.cloneLocation(request.destination),
      freight: { ...request.freight },
      ...(request.freight_items
        ? { freight_items: request.freight_items.map((item) => ({ ...item })) }
        : {}),
    };
  }

  private cloneConditions(conditions: SpotQuoteConditionsDraft): SpotQuoteConditionsDraft {
    return {
      currency: conditions.currency === 'USD' ? 'USD' : 'CAD',
      valid_until: conditions.valid_until ?? '',
      service_level: conditions.service_level ?? '',
      equipment_notes: conditions.equipment_notes ?? '',
      customer_notes: conditions.customer_notes ?? '',
      internal_notes: conditions.internal_notes ?? '',
    };
  }

  private isCurrentRatingRequest(
    request: DispatchRatingRequest,
    current: DispatchRatingRequest | null,
  ): boolean {
    return Boolean(current && JSON.stringify(current) === JSON.stringify(request));
  }

  private currentQuoteRequest(
    state: FreightCommandWorkspaceState,
    ignoreAcceptance: boolean,
  ): DispatchCreateCurrentQuoteRequest | null {
    const response = state.rate_response;
    const total = response?.quote.total;
    const conditions = response?.quote.conditions ?? [];
    const currency = (total?.currency || response?.quote.currency || '').trim().toUpperCase();
    if (
      state.rate_state !== 'rated'
      || !state.rating_request
      || !response?.ok
      || response.status !== 'rated'
      || response.quote.status !== 'calculated'
      || !total
      || !Number.isInteger(total.amount_cents)
      || !/^[A-Z]{3}$/.test(currency)
      || conditions.length === 0
      || (!ignoreAcceptance && !state.authoritative_conditions_accepted)
    ) {
      return null;
    }

    const customerUuid = state.customer_contact_uuid.trim()
      || state.rating_request.customer_contact_uuid?.trim()
      || '';
    return {
      source: 'create_quote',
      ...(customerUuid ? { customer_contact_uuid: customerUuid } : {}),
      rate_request: this.cloneRatingRequest(state.rating_request),
      conditions_accepted: true,
      expected_total_amount_cents: total.amount_cents,
      expected_currency: currency,
    };
  }

  private isCurrentQuoteCommand(
    state: FreightCommandWorkspaceState,
    command: FreightCommandQuoteCreateCommand,
  ): boolean {
    return state.quote_create_idempotency_key === command.idempotency_key
      && state.quote_create_fingerprint === command.fingerprint;
  }
}
